import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { describeApiError } from '@/api/problemDetails'
import type { TenantResponse } from '@/api/responses'
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  Input,
} from '@/components/ui'
import { useDeleteTenant } from '@/features/tenants/queries'

export interface DangerZoneCardProps {
  readonly tenant: TenantResponse
}

/**
 * Zona de perigo (só Owner): exclui a organização. A exclusão nunca é one-click — abre um diálogo que exige
 * digitar o identificador exato (slug) para habilitar o botão, no estilo do GitHub. Em sucesso, o cache de
 * tenants é invalidado (o soft-delete já removeu a org da lista) e o usuário é levado para o onboarding — os
 * guards então o redirecionam para outra organização, se houver. Nenhuma infraestrutura é citada.
 */
export function DangerZoneCard({ tenant }: DangerZoneCardProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [confirmValue, setConfirmValue] = useState('')
  const mutation = useDeleteTenant(tenant.id, () => {
    void navigate({ to: '/onboarding' })
  })

  const canConfirm = confirmValue.trim() === tenant.slug

  const close = (): void => {
    if (mutation.isPending) {
      return
    }
    setOpen(false)
    setConfirmValue('')
    mutation.reset()
  }

  const confirmDelete = (): void => {
    if (!canConfirm) {
      return
    }
    // A navegacao pos-sucesso e feita pelo hook (useDeleteTenant) — resiste ao desmonte deste card apos a exclusao.
    mutation.mutate()
  }

  const [firstMessage] = describeApiError(mutation.error)

  return (
    <Card className="border-status-danger/40">
      <CardHeader>
        <CardTitle className="text-status-danger">{t('settings.dangerZone.title')}</CardTitle>
        <CardDescription>{t('settings.dangerZone.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <Button
          variant="danger"
          onClick={() => {
            setOpen(true)
          }}
        >
          {t('settings.dangerZone.deleteButton')}
        </Button>
      </CardContent>

      {open ? (
        <Dialog
          open={open}
          onClose={close}
          title={t('settings.dangerZone.confirmTitle')}
          description={t('settings.dangerZone.confirmDescription')}
          footer={
            <>
              <Button variant="ghost" onClick={close} disabled={mutation.isPending}>
                {t('cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={confirmDelete}
                loading={mutation.isPending}
                disabled={!canConfirm}
              >
                {t('settings.dangerZone.confirmButton')}
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3">
            {firstMessage !== undefined ? <Alert variant="danger" title={firstMessage} /> : null}
            <Input
              id="danger-confirm-slug"
              label={t('settings.dangerZone.confirmPrompt', { slug: tenant.slug })}
              value={confirmValue}
              autoComplete="off"
              onChange={(event) => {
                setConfirmValue(event.target.value)
                if (mutation.isError) {
                  mutation.reset()
                }
              }}
            />
          </div>
        </Dialog>
      ) : null}
    </Card>
  )
}
